import React, { useState } from 'react';
import {
  IAuditoriaPayrollDriftResultado,
  IAuditoriaPayrollDriftItem,
  IAuditoriaPayrollDriftLancamento,
  IAuditoriaPayrollDriftFaixas,
  IContaPayrollAnalisada,
} from '../interfaces/IAuditoriaPayrollDrift';

interface Props {
  resultado: IAuditoriaPayrollDriftResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPercent = (value?: number) =>
  `${(value || 0).toFixed(2)}%`;

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'              ? 'badge-light-danger' :
    status === 'ALERTA'            ? 'badge-light-warning' :
    status === 'BASE_INSUFICIENTE' ? 'badge-light-secondary' :
                                     'badge-light-info';
  const label = status === 'BASE_INSUFICIENTE' ? 'Base Insuficiente' : status;
  return <span className={`badge ${cls}`}>{label}</span>;
};

// COR-227: rótulo/cor por papel da conta na análise (base, encargo comparável ou excluída).
const PapelBadge: React.FC<{ papel: string }> = ({ papel }) => {
  if (papel === 'BASE') return <span className='badge badge-light-primary'>Base (Salário)</span>;
  if (papel === 'ENCARGO_COMPARAVEL') return <span className='badge badge-light-dark'>Encargo Comparável</span>;
  return <span className='badge badge-light-secondary'>Excluída</span>;
};

const TabelaContasAnalisadas: React.FC<{ contas: IContaPayrollAnalisada[] }> = ({ contas }) => (
  <div className='card border-0 bg-white shadow-sm p-3 mb-4'>
    <div className='fw-bold fs-6 mb-2'>
      Contas Analisadas ({contas.length})
    </div>
    <div className='text-muted fs-8 mb-3'>
      Todas as contas do universo de folha (Obrigações com o Pessoal/Sociais e Provisões de
      Férias/13º) localizadas na ECD, com o papel exercido na análise e o motivo quando excluída.
    </div>
    {contas.length === 0 ? (
      <div className='text-muted fs-8 fst-italic'>Nenhuma conta do universo de folha foi localizada na ECD para o período selecionado.</div>
    ) : (
      <div className='table-responsive'>
        <table className='table table-sm table-row-dashed table-row-gray-200 align-middle gs-0 gy-1 fs-9'>
          <thead>
            <tr className='fw-bolder text-muted'>
              <th>Empresa</th>
              <th>Conta / Descrição</th>
              <th>Grupo de Folha</th>
              <th className='text-center'>Papel</th>
              <th className='text-center'>Meses c/ Movimento</th>
              <th>Motivo da Exclusão</th>
            </tr>
          </thead>
          <tbody>
            {contas.map((c, i) => (
              <tr key={i}>
                <td>{c.empresaNome}</td>
                <td>
                  <div className='fw-bold'>{c.descricaoConta}</div>
                  <div className='text-muted fs-9'>{c.contaContabil} · {c.contaReferencial}</div>
                </td>
                <td>{c.descricaoGrupoFolha}</td>
                <td className='text-center'><PapelBadge papel={c.papel} /></td>
                <td className='text-center'>{c.mesesComMovimento}</td>
                <td className='text-muted'>{c.motivoExclusao || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const TipoBadge: React.FC<{ tipo: string }> = ({ tipo }) => {
  if (tipo === 'AUSENCIA_ENCARGO')
    return <span className='badge badge-light-danger'>Ausência de Encargo</span>;
  return <span className='badge badge-light-warning'>Drift Alto</span>;
};

// COR-196/COR-197: identifica visualmente a natureza do encargo da ocorrência. Desde a
// COR-197, só o grupo ENCARGO_SOCIAL (FGTS/INSS) chega a gerar ocorrência — os demais
// grupos (13º, Férias, Aviso Prévio, Benefícios) não têm conta de encargo compatível no
// plano referencial e não entram mais na comparação de drift.
const GrupoFolhaBadge: React.FC<{ grupo: string; descricao: string }> = ({ grupo, descricao }) => {
  const cls = grupo === 'ENCARGO_SOCIAL' ? 'badge-light-dark' : 'badge-light-secondary';
  return <span className={`badge ${cls}`}>{descricao || grupo}</span>;
};

// COR-198: faixa de tolerância exibida a partir do mesmo dado usado no cálculo (não mais um
// texto fixo no frontend) — permite ao usuário homologar a regra sem inferir os limiares.
const FaixaClassificacaoLegend: React.FC<{ faixas: IAuditoriaPayrollDriftFaixas }> = ({ faixas }) => (
  <div className='d-flex flex-wrap gap-2 align-items-center'>
    <span className='badge badge-light-success'>CONCILIADO: Drift &lt; {faixas.limiteAviso.toFixed(0)}%</span>
    <span className='badge badge-light-info'>AVISO: {faixas.limiteAviso.toFixed(0)}% ≤ Drift &lt; {faixas.limiteAlerta.toFixed(0)}%</span>
    <span className='badge badge-light-warning'>ALERTA: {faixas.limiteAlerta.toFixed(0)}% ≤ Drift ≤ {faixas.limiteErro.toFixed(0)}%</span>
    <span className='badge badge-light-danger'>ERRO: Drift &gt; {faixas.limiteErro.toFixed(0)}%</span>
  </div>
);

const TabelaLancamentos: React.FC<{ titulo: string; lancamentos: IAuditoriaPayrollDriftLancamento[] }> = ({ titulo, lancamentos }) => (
  <div className='mb-3'>
    <div className='fw-bold text-muted fs-8 mb-1'>{titulo} ({lancamentos.length} lançamento(s))</div>
    {lancamentos.length === 0 ? (
      <div className='text-muted fs-9 fst-italic'>Nenhum lançamento na competência.</div>
    ) : (
      <div className='table-responsive' style={{ maxHeight: 200, overflowY: 'auto' }}>
        <table className='table table-sm table-row-dashed table-row-gray-200 align-middle gs-0 gy-1 fs-9'>
          <thead>
            <tr className='fw-bolder text-muted'>
              <th>Data</th>
              <th>Histórico</th>
              <th className='text-end'>Valor</th>
            </tr>
          </thead>
          <tbody>
            {lancamentos.map((l, i) => (
              <tr key={i}>
                <td>{l.data}</td>
                <td>{l.historico}</td>
                <td className='text-end'>{formatCurrency(l.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const ItemRow: React.FC<{ item: IAuditoriaPayrollDriftItem; faixas: IAuditoriaPayrollDriftFaixas }> = ({ item, faixas }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr>
        <td>
          <button
            className='btn btn-sm btn-icon btn-light me-2'
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Recolher' : 'Expandir'}
          >
            <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} />
          </button>
          <span className='fw-bold'>{item.descricaoConta}</span>
          <br /><span className='text-muted fs-8'>{item.contaReferencial}</span>
        </td>
        <td className='text-center'>
          <GrupoFolhaBadge grupo={item.grupoFolha} descricao={item.descricaoGrupoFolha} />
        </td>
        <td className='text-center'>
          <span className='badge badge-light-primary'>{item.categoria}</span>
        </td>
        <td className='text-center fw-bold'>{item.mesAno}</td>
        <td className='text-end'>
          <div className='fw-bold'>{formatCurrency(item.valorSalario)}</div>
          <div className='text-muted fs-8'>Sal: {formatPercent(item.varPercentualSalario)}</div>
        </td>
        <td className='text-end'>
          <div className='fw-bold'>{formatCurrency(item.valorEncargo)}</div>
          <div className='text-muted fs-8'>Enc: {formatPercent(item.varPercentualEncargo)}</div>
        </td>
        <td className='text-end fw-bolder text-danger'>{formatPercent(item.drift)}</td>
        <td className='text-end fw-bold'>
          {item.impactoFinanceiro > 0 ? (
            <span className='text-danger'>{formatCurrency(item.impactoFinanceiro)}</span>
          ) : (
            <span className='text-muted'>—</span>
          )}
        </td>
        <td className='text-center'><TipoBadge tipo={item.tipoOcorrencia} /></td>
        <td className='text-center'><StatusBadge status={item.status} /></td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={10} className='bg-light'>
            <div className='p-4'>
              {/* Composição: qual conta entrou como base e qual entrou como encargo */}
              <div className='row g-3 mb-3'>
                <div className='col-md-6'>
                  <div className='card border-0 bg-white shadow-sm p-3'>
                    <div className='text-muted fs-8'>Conta Base — Natureza: Folha Normal — {item.mesAno}</div>
                    <div className='fw-bold'>{item.descricaoContaBase} <span className='text-muted fs-9'>({item.contaSalarioRef})</span></div>
                    <div className='fs-8 text-muted mt-1'>
                      Atual: <strong>{formatCurrency(item.valorSalario)}</strong> ·{' '}
                      Anterior ({item.mesAnoBaseline}): <strong>{formatCurrency(item.valorSalarioAnterior)}</strong>
                    </div>
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='card border-0 bg-white shadow-sm p-3'>
                    <div className='text-muted fs-8'>Conta de Encargo — Natureza: {item.descricaoGrupoFolha} — {item.mesAno}</div>
                    <div className='fw-bold'>{item.descricaoConta} <span className='text-muted fs-9'>({item.contaReferencial})</span></div>
                    <div className='fs-8 text-muted mt-1'>
                      Atual: <strong>{formatCurrency(item.valorEncargo)}</strong> ·{' '}
                      Anterior ({item.mesAnoBaseline}): <strong>{formatCurrency(item.valorEncargoAnterior)}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className='row g-3 mb-3'>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Variação Salário</div>
                    <div className='fw-bolder fs-5'>{formatPercent(item.varPercentualSalario)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Variação Encargo</div>
                    <div className='fw-bolder fs-5'>{formatPercent(item.varPercentualEncargo)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Valor Esperado</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.valorEsperado)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Diferença Nominal</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.diferencaNominal)}</div>
                  </div>
                </div>
              </div>

              {item.status === 'ERRO' && (
                <div className='row g-3 mb-3'>
                  <div className='col-md-6'>
                    <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                      <div className='text-muted fs-8'>Taxa SELIC Mensal ({item.origemTaxaSelic})</div>
                      <div className='fw-bolder fs-5'>{formatPercent(item.taxaSelicMensal)}</div>
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                      <div className='text-muted fs-8'>Impacto Financeiro (DiferençaNominal × SELIC/100)</div>
                      <div className='fw-bolder fs-5 text-danger'>{formatCurrency(item.impactoFinanceiro)}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Faixa de classificação aplicada — critério auditável no detalhe da ocorrência */}
              <div className='card border-0 bg-white shadow-sm p-3 mb-3'>
                <div className='fw-bold fs-8 text-muted mb-2'>Faixa de Classificação Aplicada</div>
                <FaixaClassificacaoLegend faixas={faixas} />
              </div>

              {/* Memória de cálculo completa — lançamentos individuais da ECD */}
              <div className='card border-0 bg-white shadow-sm p-3 mb-3'>
                <div className='fw-bold fs-8 text-muted mb-2'>Memória de Cálculo — Lançamentos da ECD ({item.mesAno})</div>
                <TabelaLancamentos titulo={`Base (${item.descricaoContaBase})`} lancamentos={item.lancamentosBase} />
                <TabelaLancamentos titulo={`Encargo (${item.descricaoConta})`} lancamentos={item.lancamentosEncargo} />
              </div>

              {item.formulaAplicada && (
                <div className='alert alert-light border border-secondary p-2 fs-9 mb-2'>
                  <strong>Fórmula Aplicada:</strong> {item.formulaAplicada}
                </div>
              )}

              {item.criterioClassificacao && (
                <div className='alert alert-light border border-secondary p-2 fs-9 mb-2'>
                  <strong>Critério de Classificação:</strong> {item.criterioClassificacao}
                </div>
              )}

              <div className={`alert p-3 fs-8 ${
                item.status === 'ERRO'   ? 'alert-light-danger border border-danger' :
                item.status === 'ALERTA' ? 'alert-light-warning border border-warning' :
                                           'alert-light-info border border-info'
              }`}>
                {item.mensagemDiagnostico}
              </div>

              {item.mensagemDirecionamento && (
                <div className='alert alert-light border border-secondary p-2 fs-9 mt-2'>
                  <strong>Direcionamento:</strong> {item.mensagemDirecionamento}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaPayrollDriftDetalhe: React.FC<Props> = ({ resultado }) => {
  if (!resultado) return null;

  return (
    <div className='card card-custom card-stretch mb-5'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-4 mb-1'>
            Detalhamento — Variação Móvel de Folha (Payroll Drift Analytics)
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            {resultado.itens.length} ocorrência(s) ·{' '}
            {resultado.totalErros} erro(s) · {resultado.totalAlertas} alerta(s) · {resultado.totalAvisos} aviso(s)
            <> · <StatusBadge status={resultado.statusGeral} /></>
            {resultado.impactoFinanceiroTotal > 0 && (
              <> · Passivo SELIC:{' '}
                <strong className='text-danger'>
                  {resultado.impactoFinanceiroTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </strong>
              </>
            )}
          </span>
        </h3>
      </div>

      <div className='card-body pt-3'>
        {/* COR-227: mensagem diferencia "ausência real de variação anormal" (cálculo rodou) de
            "base comparativa insuficiente" (não foi possível calcular) — nunca mais um "situação
            regular" genérico independente do que de fato aconteceu. */}
        {resultado.mensagemDirecionamento && (
          <div className={`alert p-3 fs-7 mb-4 ${
            resultado.statusGeral === 'BASE_INSUFICIENTE' ? 'alert-light-secondary border border-secondary' :
            resultado.statusGeral === 'CONCILIADO'        ? 'alert-light-success border border-success' :
            resultado.statusGeral === 'ERRO'               ? 'alert-light-danger border border-danger' :
            resultado.statusGeral === 'ALERTA'             ? 'alert-light-warning border border-warning' :
                                                              'alert-light-info border border-info'
          }`}>
            <strong>{resultado.statusGeral === 'BASE_INSUFICIENTE' ? 'Base Comparativa Insuficiente' : resultado.statusGeral}:</strong>{' '}
            {resultado.mensagemDirecionamento}
          </div>
        )}

        <TabelaContasAnalisadas contas={resultado.contasAnalisadas || []} />

        <div className='mb-4'>
          <div className='fw-bold fs-8 text-muted mb-2'>Faixa de Classificação Aplicada</div>
          <FaixaClassificacaoLegend faixas={resultado.faixasClassificacao} />
        </div>

        {resultado.itens.length === 0 ? (
          <div className={`fw-bold fs-5 text-center py-8 ${resultado.statusGeral === 'BASE_INSUFICIENTE' ? 'text-muted' : 'text-success'}`}>
            {resultado.statusGeral === 'BASE_INSUFICIENTE'
              ? '— Nenhum par comparável válido localizado. Ver "Contas Analisadas" acima.'
              : '✓ Nenhuma variação anormal de folha detectada. Situação regular.'}
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Conta / Descrição</th>
                  <th className='text-center'>Grupo Folha</th>
                  <th className='text-center'>Categoria</th>
                  <th className='text-center'>Mês/Ano</th>
                  <th className='text-end'>Salário</th>
                  <th className='text-end'>Encargo</th>
                  <th className='text-end'>Drift</th>
                  <th className='text-end'>Impacto</th>
                  <th className='text-center'>Ocorrência</th>
                  <th className='text-center'>Status</th>
                </tr>
              </thead>
              <tbody>
                {resultado.itens.map((item, i) => (
                  <ItemRow key={i} item={item} faixas={resultado.faixasClassificacao} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {resultado.fundamentacaoLegal && (
          <div className='alert alert-light border border-secondary p-3 fs-9 mt-4'>
            <strong>Fundamentação Legal:</strong> {resultado.fundamentacaoLegal}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaPayrollDriftDetalhe;
